"use client"

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuthToken } from "@/hooks/use-auth"

// API client configuration - empty string means same origin (Vite proxies /api to Express)
const API_BASE = ""

// ============================================
// Type Definitions
// ============================================

export type PromptStatus = "DRAFT" | "ACTIVE" | "DEPRECATED"

export interface PromptVariable {
  name: string
  description?: string
  required: boolean
  defaultValue?: string
}

export interface Prompt {
  id: string
  key: string
  name: string
  description?: string | null
  category: string
  type: string
  status: PromptStatus
  isDefault: boolean
  isArchived: boolean
  createdBy?: {
    id: string
    name: string | null
    email: string
  }
  updatedBy?: {
    id: string
    name: string | null
    email: string
  }
  activeVersion: string
  versionsCount: number
  createdAt: string
  updatedAt: string
}

export interface PromptDetail extends Prompt {
  systemPrompt: string
  userPrompt?: string | null
  variables?: PromptVariable[] | null
  temperature?: number | null
  maxTokens?: number | null
  modelHints?: string[]
  versions: PromptVersion[]
}

export interface PromptVersion {
  id: string
  promptId: string
  version: string
  status: PromptStatus
  systemPrompt: string
  userPrompt?: string | null
  variables?: PromptVariable[] | null
  temperature?: number | null
  maxTokens?: number | null
  changelog?: string | null
  createdBy?: {
    id: string
    name: string | null
    email: string
  }
  createdAt: string
}

export interface CreatePromptInput {
  key: string
  name: string
  description?: string
  category: string
  type?: string
  systemPrompt: string
  userPrompt?: string
  variables?: PromptVariable[]
  temperature?: number
  maxTokens?: number
  modelHints?: string[]
}

export interface UpdatePromptInput {
  name?: string
  description?: string | null
  category?: string
  type?: string
  systemPrompt?: string
  userPrompt?: string | null
  variables?: PromptVariable[] | null
  temperature?: number | null
  maxTokens?: number | null
  modelHints?: string[]
  changelog?: string
}

export interface PromptQueryParams {
  page?: number
  pageSize?: number
  search?: string
  category?: string
  status?: string
  showArchived?: boolean
}

// ============================================
// API Response Types
// ============================================

interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  meta: {
    pagination: {
      total: number
      page: number
      pageSize: number
      totalPages: number
    }
  }
}

interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

// ============================================
// Fetch helpers
// ============================================

async function fetchPrompts(
  token: string | null,
  params?: PromptQueryParams
): Promise<{ prompts: Prompt[]; total: number }> {
  if (!token) return { prompts: [], total: 0 }

  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set("page", params.page.toString())
  if (params?.pageSize) searchParams.set("pageSize", params.pageSize.toString())
  if (params?.search) searchParams.set("search", params.search)
  if (params?.category) searchParams.set("category", params.category)
  if (params?.status) searchParams.set("status", params.status)
  if (params?.showArchived) searchParams.set("showArchived", "true")

  const response = await fetch(
    `${API_BASE}/api/prompts?${searchParams.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  )

  if (!response.ok) {
    if (response.status === 401) return { prompts: [], total: 0 }
    throw new Error("Failed to fetch prompts")
  }

  const data: PaginatedResponse<Prompt> = await response.json()
  return {
    prompts: data.data,
    total: data.meta.pagination.total,
  }
}

async function fetchPrompt(
  promptId: string,
  token: string | null
): Promise<PromptDetail | null> {
  if (!token) return null

  const response = await fetch(`${API_BASE}/api/prompts/${promptId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    if (response.status === 404) return null
    throw new Error("Failed to fetch prompt")
  }

  const data: ApiResponse<PromptDetail> = await response.json()
  return data.data
}

async function createPrompt(
  input: CreatePromptInput,
  token: string | null
): Promise<Prompt> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/prompts`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to create prompt")
  }

  const data: ApiResponse<Prompt> = await response.json()
  return data.data
}

async function updatePrompt(
  promptId: string,
  input: UpdatePromptInput,
  token: string | null
): Promise<Prompt> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/prompts/${promptId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to update prompt")
  }

  const data: ApiResponse<Prompt> = await response.json()
  return data.data
}

async function deletePrompt(promptId: string, token: string | null): Promise<void> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/prompts/${promptId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to delete prompt")
  }
}

async function restorePrompt(
  promptId: string,
  token: string | null
): Promise<Prompt> {
  if (!token) throw new Error("Not authenticated")

  const response = await fetch(`${API_BASE}/api/prompts/${promptId}/restore`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error?.message || "Failed to restore prompt")
  }

  const data = await response.json()
  return data.data
}

async function changePromptStatus(
  promptId: string,
  status: PromptStatus,
  token: string | null
): Promise<{ status: PromptStatus }> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/prompts/${promptId}/status`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to change status")
  }

  const data = await response.json()
  return data.data
}

async function setDefaultPrompt(
  promptId: string,
  isDefault: boolean,
  token: string | null
): Promise<{ isDefault: boolean }> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/prompts/${promptId}/set-default`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ isDefault }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to set default")
  }

  const data = await response.json()
  return data.data
}

async function duplicatePrompt(
  promptId: string,
  newKey: string,
  newName?: string,
  token?: string | null
): Promise<Prompt> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/prompts/${promptId}/duplicate`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ newKey, newName }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to duplicate prompt")
  }

  const data: ApiResponse<Prompt> = await response.json()
  return data.data
}

async function fetchPromptVersions(
  promptId: string,
  token: string | null
): Promise<PromptVersion[]> {
  if (!promptId) return []

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}/api/prompts/${promptId}/versions`, {
    headers,
  })

  if (!response.ok) {
    throw new Error("Failed to fetch versions")
  }

  const json = await response.json()
  return json.data || []
}

async function activatePromptVersion(
  promptId: string,
  versionId: string,
  token: string | null
): Promise<{ id: string; version: string; status: string }> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(
    `${API_BASE}/api/prompts/${promptId}/versions/${versionId}/activate`,
    {
      method: "PATCH",
      headers,
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error?.message || "Failed to activate version")
  }

  const json = await response.json()
  return json.data
}

// ============================================
// Query Hooks
// ============================================

export function usePrompts(params?: PromptQueryParams) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["prompts", params],
    queryFn: () => fetchPrompts(token, params),
    enabled: !!token,
  })
}

export function usePrompt(promptId: string) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["prompts", promptId],
    queryFn: () => fetchPrompt(promptId, token),
    enabled: !!promptId && !!token,
  })
}

export function usePromptVersions(promptId: string) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["prompts", promptId, "versions"],
    queryFn: () => fetchPromptVersions(promptId, token),
    enabled: !!promptId,
  })
}

// ============================================
// Mutation Hooks
// ============================================

export function useCreatePrompt() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (input: CreatePromptInput) => createPrompt(input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompts"] })
    },
  })
}

export function useUpdatePrompt() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: ({ promptId, ...input }: UpdatePromptInput & { promptId: string }) =>
      updatePrompt(promptId, input, token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["prompts"] })
      queryClient.invalidateQueries({ queryKey: ["prompts", variables.promptId] })
    },
  })
}

export function useDeletePrompt() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (promptId: string) => deletePrompt(promptId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompts"] })
    },
  })
}

export function useRestorePrompt() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (promptId: string) => restorePrompt(promptId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompts"] })
    },
  })
}

export function useChangePromptStatus() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: ({ promptId, status }: { promptId: string; status: PromptStatus }) =>
      changePromptStatus(promptId, status, token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["prompts"] })
      queryClient.invalidateQueries({ queryKey: ["prompts", variables.promptId] })
    },
  })
}

export function useSetDefaultPrompt() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: ({ promptId, isDefault }: { promptId: string; isDefault: boolean }) =>
      setDefaultPrompt(promptId, isDefault, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompts"] })
    },
  })
}

export function useDuplicatePrompt() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: ({ promptId, newKey, newName }: { promptId: string; newKey: string; newName?: string }) =>
      duplicatePrompt(promptId, newKey, newName, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompts"] })
    },
  })
}

export function useActivatePromptVersion(promptId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (versionId: string) => activatePromptVersion(promptId, versionId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompts"] })
      queryClient.invalidateQueries({ queryKey: ["prompts", promptId] })
      queryClient.invalidateQueries({ queryKey: ["prompts", promptId, "versions"] })
    },
  })
}
